import clinicInfo from '../../../config/clinicInfo';
import { supabase } from '../../../config/supabase';
import type { Client } from '../../../services/UserService';
import { getClientBrandingPublicUrl } from '../../../utils/storage';
import { DEFAULT_TIME_ZONE, isValidTimeZone } from '../../../utils/timeZone';
import type { ClientReportSettings, ResolvedClientReportBranding } from '../types';

const pickFirst = (...values: Array<string | null | undefined>) =>
  values.find((value) => typeof value === 'string' && value.trim().length > 0)?.trim() ?? '';

const buildAddressLine1 = (input: {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
}) => {
  const base = [input.street, input.number].filter(Boolean).join(', ');
  const suffix = [input.complement, input.district].filter(Boolean).join(' - ');
  return [base, suffix].filter(Boolean).join(', ').trim();
};

const buildAddressLine2 = (input: {
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}) =>
  [input.city && input.state ? `${input.city} - ${input.state}` : input.city || input.state, input.postalCode ? `CEP: ${input.postalCode}` : '']
    .filter(Boolean)
    .join(' | ')
    .trim();

const buildContactLine = (input: { phone?: string | null; email?: string | null }) =>
  [input.phone, input.email].filter(Boolean).join(' | ').trim();

const normalizeNullable = (value?: string | null) => {
  const normalized = value?.trim();
  return normalized ? normalized : null;
};

const resolveStoredTimeZone = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return DEFAULT_TIME_ZONE;
  if (!isValidTimeZone(value)) {
    throw new Error('A clínica possui um fuso horário inválido no banco. Corrija a configuração antes de continuar.');
  }
  return value.trim();
};

const resolveStoredLogoUrl = (
  path?: string | null,
  legacyUrl?: string | null,
) => {
  if (path) {
    return getClientBrandingPublicUrl(path);
  }

  return legacyUrl ?? '';
};

export class ClientReportSettingsService {
  static async getByClientId(clientId: string): Promise<ClientReportSettings | null> {
    const { data, error } = await supabase
      .from('client_report_settings')
      .select('*')
      .eq('clientId', clientId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao carregar client_report_settings:', error);
      throw error;
    }

    return (data as ClientReportSettings | null) ?? null;
  }

  /**
   * Resolve somente o fuso operacional do tenant para servicos sem contexto React.
   * Tenant sem linha de configuracao preserva o legado America/Sao_Paulo. Erro
   * de schema/deploy, rede ou RLS falha fechado: um writer nunca pode confundir
   * indisponibilidade da configuracao com um fuso confirmado.
   */
  static async getTimeZoneByClientId(clientId: string): Promise<string> {
    const { data, error } = await supabase
      .from('client_report_settings')
      .select('timeZone')
      .eq('clientId', clientId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao carregar o fuso horario da clinica:', error);
      throw error;
    }

    return resolveStoredTimeZone((data as { timeZone?: string | null } | null)?.timeZone);
  }

  static async upsertForClient(
    clientId: string,
    input: Partial<ClientReportSettings>,
  ): Promise<ClientReportSettings> {
    const payload: Partial<ClientReportSettings> & { clientId: string; updatedAt: string } = {
      clientId,
      updatedAt: new Date().toISOString(),
      reportDisplayName: normalizeNullable(input.reportDisplayName),
      legalName: normalizeNullable(input.legalName),
      cnpj: normalizeNullable(input.cnpj),
      reportSubtitle: normalizeNullable(input.reportSubtitle),
      logoPrintPath: normalizeNullable(input.logoPrintPath),
      logoPrintUrl: normalizeNullable(input.logoPrintUrl),
      logoMarkPath: normalizeNullable(input.logoMarkPath),
      logoMarkUrl: normalizeNullable(input.logoMarkUrl),
      logoWordmarkPath: normalizeNullable(input.logoWordmarkPath),
      logoWordmarkUrl: normalizeNullable(input.logoWordmarkUrl),
      logoAlt: normalizeNullable(input.logoAlt),
      addressStreet: normalizeNullable(input.addressStreet),
      addressNumber: normalizeNullable(input.addressNumber),
      addressComplement: normalizeNullable(input.addressComplement),
      addressDistrict: normalizeNullable(input.addressDistrict),
      addressCity: normalizeNullable(input.addressCity),
      addressState: normalizeNullable(input.addressState),
      addressPostalCode: normalizeNullable(input.addressPostalCode),
      ...(input.timeZone !== undefined
        ? { timeZone: resolveStoredTimeZone(input.timeZone) }
        : {}),
      contactPhone: normalizeNullable(input.contactPhone),
      contactPhoneFull: normalizeNullable(input.contactPhoneFull),
      contactWhatsapp: normalizeNullable(input.contactWhatsapp),
      contactWhatsappFull: normalizeNullable(input.contactWhatsappFull),
      contactEmail: normalizeNullable(input.contactEmail),
      footerLegalText: normalizeNullable(input.footerLegalText),
    };

    const { data, error } = await supabase
      .from('client_report_settings')
      .upsert(payload, { onConflict: 'clientId' })
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao salvar client_report_settings:', error);
      throw error;
    }

    return data as ClientReportSettings;
  }

  static async resolveForClient(clientId: string, selectedClient: Client | null): Promise<ResolvedClientReportBranding> {
    let settings: ClientReportSettings | null = null;

    try {
      settings = await this.getByClientId(clientId);
    } catch (error) {
      console.warn('Falha ao buscar branding do tenant para relatórios. Aplicando fallback.', error);
    }

    const logoWordmarkFallback = pickFirst(
      selectedClient?.logo_wordmark_url,
      selectedClient?.clientLogoWordmarkUrl,
      selectedClient?.logo_url,
      selectedClient?.clientLogoUrl,
      clinicInfo.reportBranding.logoPrint,
      clinicInfo.logoPrint
    );

    const logoMarkFallback = pickFirst(
      selectedClient?.logo_url,
      selectedClient?.clientLogoUrl,
      clinicInfo.reportBranding.logoMark,
      clinicInfo.logo
    );

    const displayName = pickFirst(
      settings?.reportDisplayName,
      selectedClient?.clientName,
      clinicInfo.reportBranding.displayName,
      clinicInfo.nomeFantasia
    );

    const legalName = pickFirst(settings?.legalName, clinicInfo.razaoSocial);
    const cnpj = pickFirst(settings?.cnpj, clinicInfo.cnpj);
    const reportSubtitle = pickFirst(settings?.reportSubtitle, clinicInfo.especialidades);
    const logoPrintUrl = pickFirst(
      resolveStoredLogoUrl(settings?.logoPrintPath, settings?.logoPrintUrl),
      resolveStoredLogoUrl(settings?.logoWordmarkPath, settings?.logoWordmarkUrl),
      logoWordmarkFallback,
    );
    const logoMarkUrl = pickFirst(
      resolveStoredLogoUrl(settings?.logoMarkPath, settings?.logoMarkUrl),
      logoMarkFallback,
    );
    const logoWordmarkUrl = pickFirst(
      resolveStoredLogoUrl(settings?.logoWordmarkPath, settings?.logoWordmarkUrl),
      resolveStoredLogoUrl(settings?.logoPrintPath, settings?.logoPrintUrl),
      logoWordmarkFallback,
    );
    const logoAlt = pickFirst(settings?.logoAlt, displayName, clinicInfo.reportBranding.logoAlt);

    const addressLine1 = pickFirst(
      buildAddressLine1({
        street: settings?.addressStreet,
        number: settings?.addressNumber,
        complement: settings?.addressComplement,
        district: settings?.addressDistrict,
      }),
      clinicInfo.endereco.enderecoCompleto
    );

    const addressLine2 = pickFirst(
      buildAddressLine2({
        city: settings?.addressCity,
        state: settings?.addressState,
        postalCode: settings?.addressPostalCode,
      }),
      `${clinicInfo.endereco.cidade} - ${clinicInfo.endereco.estado} | CEP: ${clinicInfo.endereco.cep}`
    );

    const contactLine = pickFirst(
      buildContactLine({
        phone: settings?.contactPhoneFull || settings?.contactPhone || settings?.contactWhatsappFull || settings?.contactWhatsapp,
        email: settings?.contactEmail,
      }),
      `${clinicInfo.contato.telefone} | ${clinicInfo.contato.email}`
    );

    return {
      clientId,
      source: settings ? 'db' : 'fallback',
      displayName,
      legalName,
      cnpj,
      reportSubtitle,
      logoPrintUrl,
      logoMarkUrl,
      logoWordmarkUrl,
      logoAlt,
      addressLine1,
      addressLine2,
      contactLine,
      footerLegalLine: pickFirst(settings?.footerLegalText, `${legalName} - CNPJ: ${cnpj}`),
      rawSettings: settings,
    };
  }
}
